import { ColorTokenKey } from '@faire/web-api--source/indigofair/data/ColorTokenKey';
import { IImageElement } from '@faire/web-api--source/indigofair/data/IImageElement';
import { ITextElement } from '@faire/web-api--source/indigofair/data/ITextElement';

export namespace IImageTile {
  export namespace IImageTileStyle {
    /**
     * Return an instance of IImageTileStyle with the provided fields. Any field not
     * listed will be populated with an empty value (an empty array,
     * or undefined, whichever is valid for the type of the value.)
     */
    export const build = (
      partial?: Partial<IImageTileStyle>
    ): IImageTileStyle =>
      ({
        animation_on_hover: [],
        ...(partial ?? {}),
      }) as IImageTileStyle;
  }

  export interface IImageTileStyle {
    variant: keyof typeof IImageTile.ImageTileVariant | undefined;
    animation_on_hover: Array<keyof typeof IImageTile.ImageTileAnimation>;
    background_color: keyof typeof ColorTokenKey | undefined;
  }

  export enum ImageTileVariant {
    IMAGE_TILE_VARIANT_UNKNOWN = 'IMAGE_TILE_VARIANT_UNKNOWN',
    /**
     * Used when the image is rectangular aspect ratio is 1:1
     */
    SQUARE_TILE = 'SQUARE_TILE',
    /**
     * Used when the image is circular with default size
     */
    CIRCLE_TILE = 'CIRCLE_TILE',
    /**
     * Used when the image is circular with small size
     */
    SMALL_CIRCLE_TILE = 'SMALL_CIRCLE_TILE',
    /**
     * Used when the image is rectangular with aspect ratio 3:4
     */
    PORTRAIT_TILE = 'PORTRAIT_TILE',
    /**
     * Used when the image is rectangular with aspect ratio 4:3
     */
    LANDSCAPE_TILE = 'LANDSCAPE_TILE',
    /**
     * Used when the tile container is rectangular with rounded corners with aspect ratio 5:4;
     * The image is rendered in the lower left corner
     */
    LANDSCAPE_IMAGE_CUTOUT = 'LANDSCAPE_IMAGE_CUTOUT',
    /**
     * Used when the image is rectangular with aspect ratio 3:1
     */
    THIN_LANDSCAPE_TILE = 'THIN_LANDSCAPE_TILE',
    /**
     * Used when the image is rectangular with aspect ratio 6:1
     */
    EXTRA_THIN_LANDSCAPE_TILE = 'EXTRA_THIN_LANDSCAPE_TILE',
  }

  export enum ImageTileAnimation {
    IMAGE_TILE_ANIMATION_UNKNOWN = 'IMAGE_TILE_ANIMATION_UNKNOWN',
    ZOOM = 'ZOOM',
    UNDERLINE = 'UNDERLINE',
  }

  /**
   * Return an instance of IImageTile with the provided fields. Any field not
   * listed will be populated with an empty value (an empty array,
   * or undefined, whichever is valid for the type of the value.)
   */
  export const build = (partial?: Partial<IImageTile>): IImageTile =>
    ({
      ...(partial ?? {}),
    }) as IImageTile;
}

/**
 * Web + App
 */
export interface IImageTile {
  /**
   * Primary text displayed on the image tile, its position depends on the tile variant; to be renamed
   */
  below_image_title: ITextElement | undefined;
  style: IImageTile.IImageTileStyle | undefined;
  image_element: IImageElement | undefined;
}
